<template>
  <div class="p-6">
    <div v-if="loading" class="text-center">Loading...</div>
    <div v-else-if="error" class="text-red-600">
      <div>{{ error }}</div>
      <div class="mt-2">
        <button @click="fetch" class="btn-secondary">Retry</button>
        <button @click="goBack" class="btn-secondary ml-2">Back</button>
      </div>
    </div>
    <div v-else-if="equipment">
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-2xl font-bold">{{ equipment.name }}</h1>
        <div class="flex space-x-2">
          <button @click="goBack" class="btn-secondary">Back</button>
          <button @click="goEdit" class="btn-primary">Edit</button>
          <button @click="showConfirm = true" class="btn-danger">Delete</button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-white p-4 rounded shadow">
          <p><strong>Type:</strong> {{ equipment.type }}</p>
          <p><strong>Model:</strong> {{ equipment.model }}</p>
          <p><strong>Serial:</strong> {{ equipment.serial_number }}</p>
          <p><strong>Status:</strong> {{ equipment.status }}</p>
          <p>
            <strong>Fuel:</strong>
            {{ equipment.requires_fuel ? equipment.fuel_type || 'Yes' : 'No' }}
          </p>
        </div>
        <div class="bg-white p-4 rounded shadow">
          <p><strong>Purchase Date:</strong> {{ equipment.purchase_date }}</p>
          <p><strong>Created By:</strong> {{ equipment.created_by || '-' }}</p>
          <p><strong>Owned By:</strong> {{ equipment.owned_by || '-' }}</p>
          <p>
            <strong>Created At:</strong>
            {{ equipment.created_at ? new Date(equipment.created_at).toLocaleString() : '-' }}
          </p>
        </div>
      </div>

      <ConfirmDialog
        :visible="showConfirm"
        title="Delete Equipment"
        message="Are you sure you want to delete this equipment? This cannot be undone."
        @confirm="doDelete"
        @cancel="showConfirm = false"
      />
    </div>
    <div v-else>No equipment found.</div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { equipmentApi } from '@/api/equipmentApi'
import type { Equipment } from '@/types/equipment'

const route = useRoute()
const router = useRouter()
const id = Number(route.params.id)
const equipment = ref<Equipment | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const showConfirm = ref(false)

const fetch = async () => {
  loading.value = true
  try {
    equipment.value = await equipmentApi.getById(id)
    error.value = null
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

const goBack = () => router.push({ name: 'EquipmentView' })
const goEdit = () => {
  if (equipment.value && equipment.value.id != null) {
    router.push({ name: 'EquipmentCreate', query: { editId: String(equipment.value.id) } })
  }
}

const doDelete = async () => {
  if (!equipment.value || equipment.value.id == null) return
  try {
    await equipmentApi.delete(equipment.value.id)
    showConfirm.value = false
    goBack()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(fetch)
</script>
